(function () {
  var SECTION_SELECTOR = '.oddit-section-product-recommendations';
  var GRABBING_CLASS = 'oddit-section-product-recommendations__track--grabbing';

  // Desktop click-and-drag scrolling (mouse only; touch scrolls natively).
  // Cards stay clickable: a drag only starts once the pointer clearly moves,
  // and the click that follows a drag is swallowed so links don't fire.
  function initDrag(track, onDragEnd) {
    var DRAG_THRESHOLD = 6;
    var isDown = false;
    var dragging = false;
    var suppressClick = false;
    var startX = 0;
    var startScroll = 0;
    var pointerId = null;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      isDown = true;
      dragging = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      pointerId = e.pointerId;
    });

    track.addEventListener('pointermove', function (e) {
      if (!isDown || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        // Nothing to scroll (e.g. mobile grid layout) — leave clicks alone.
        if (track.scrollWidth - track.clientWidth <= 2) return;
        dragging = true;
        track.classList.add(GRABBING_CLASS);
        try { track.setPointerCapture(pointerId); } catch (err) {}
      }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    });

    function endDrag(e) {
      if (!isDown || (pointerId !== null && e.pointerId !== pointerId)) return;
      isDown = false;
      if (dragging) {
        track.classList.remove(GRABBING_CLASS);
        try { track.releasePointerCapture(pointerId); } catch (err) {}
        suppressClick = true;
        if (typeof onDragEnd === 'function') onDragEnd();
      }
      dragging = false;
      pointerId = null;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (e) {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressClick = false;
      }
    }, true);

    // Always cancel the browser's native image/link drag so a click-and-hold
    // reliably starts a carousel scroll instead of dragging the image.
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  function initCarousel(section) {
    var track = section.querySelector('.oddit-section-product-recommendations__track');
    var cards = section.querySelectorAll('.oddit-section-product-recommendations__card');
    var prevBtn = section.querySelector('.oddit-section-product-recommendations__arrow--prev');
    var nextBtn = section.querySelector('.oddit-section-product-recommendations__arrow--next');

    if (!track || cards.length === 0) return;
    if (track.dataset.odditCarouselInit === 'true') return;
    track.dataset.odditCarouselInit = 'true';

    function getCardStep() {
      if (cards.length < 2) return cards[0].getBoundingClientRect().width;
      var first = cards[0].getBoundingClientRect();
      var second = cards[1].getBoundingClientRect();
      return second.left - first.left;
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function atStart() {
      return track.scrollLeft <= 2;
    }

    function atEnd() {
      return track.scrollLeft >= maxScroll() - 2;
    }

    function scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    function updateArrows() {
      // Only relevant when the track actually overflows (desktop layout).
      var scrollable = maxScroll() > 2;
      if (prevBtn) prevBtn.hidden = !scrollable || atStart();
      if (nextBtn) nextBtn.hidden = !scrollable || atEnd();
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: getCardStep(), behavior: scrollBehavior() });
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -getCardStep(), behavior: scrollBehavior() });
      });
    }

    var scrollDebounce = null;
    track.addEventListener('scroll', function () {
      if (scrollDebounce) cancelAnimationFrame(scrollDebounce);
      scrollDebounce = requestAnimationFrame(updateArrows);
    }, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateArrows).observe(track);
    } else {
      window.addEventListener('resize', updateArrows);
    }

    // After a mouse drag ends, glide smoothly to the nearest card instead of
    // letting scroll-snap re-engage with an instant jump. Snap is held off
    // during the glide so the smooth scroll isn't interrupted, then restored.
    function snapToNearest() {
      var max = maxScroll();
      if (max <= 2) return;
      var step = getCardStep();
      if (!step) return;

      var target = Math.round(track.scrollLeft / step) * step;
      target = Math.max(0, Math.min(target, max));

      track.style.scrollSnapType = 'none';
      var restore = function () { track.style.scrollSnapType = ''; };
      var fallback = setTimeout(restore, 700);
      track.addEventListener('scrollend', function onEnd() {
        clearTimeout(fallback);
        restore();
        track.removeEventListener('scrollend', onEnd);
      }, { once: true });

      track.scrollTo({ left: target, behavior: scrollBehavior() });
    }

    initDrag(track, snapToNearest);
    updateArrows();
  }

  // oddit-interactions.js wires its reveal/stagger observers once at page load and
  // never sees nodes injected afterwards, so freshly fetched cards would be stuck
  // at opacity:0. Assign the stagger indices and mark the cards visible here.
  function revealInjected(section) {
    var staggerRoots = section.querySelectorAll('[data-oddit-stagger]');
    Array.prototype.forEach.call(staggerRoots, function (container) {
      var idx = 0;
      Array.prototype.forEach.call(container.children, function (child) {
        if (child.classList && child.classList.contains('oddit-reveal')) {
          child.style.setProperty('--oddit-stagger-index', String(idx));
          idx += 1;
        }
      });
    });
    section.querySelectorAll('.oddit-reveal:not(.is-visible)').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  // Fetch recommendations through the Product Recommendations API rendered with
  // the Section Rendering API (the section's data-url carries section_id +
  // product_id + limit + intent), then swap the returned cards into the track.
  function loadRecommendations(section) {
    var url = section.dataset.url;
    var track = section.querySelector('.oddit-section-product-recommendations__track');
    if (!url || !track) return;

    var run = function () {
      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations request failed');
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var freshTrack = doc.querySelector('.oddit-section-product-recommendations__track');
          var hasCards =
            freshTrack && freshTrack.querySelector('.oddit-section-product-recommendations__card');

          if (hasCards) {
            track.innerHTML = freshTrack.innerHTML;
            revealInjected(section);
            initCarousel(section);
          } else {
            // Nothing to recommend — drop the whole section so no empty heading lingers.
            var wrapper = section.closest('.shopify-section') || section;
            wrapper.setAttribute('hidden', '');
          }
        })
        .catch(function () {});
    };

    // Defer the request until the section is near the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries, obs) {
          if (entries[0].isIntersecting) {
            obs.disconnect();
            run();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(section);
    } else {
      run();
    }
  }

  function initSection(section) {
    if (section.dataset.odditRecsInit === 'true') return;
    section.dataset.odditRecsInit = 'true';

    // When the section already has cards (it IS the Section Rendering API response,
    // or the theme editor re-rendered it with recommendations) just wire the
    // carousel; otherwise fetch them client-side.
    if (section.querySelector('.oddit-section-product-recommendations__card')) {
      initCarousel(section);
    } else {
      loadRecommendations(section);
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  });

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();
